'use client';
import React, { useCallback, useState, useEffect, Suspense } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { useTranslation } from 'react-i18next';
import { ParsedUrlQuery } from 'querystring';
import { UAParser } from 'ua-parser-js';
import { faLock } from '@fortawesome/pro-regular-svg-icons';
import { faEnvelope, faPhone, faUser } from '@fortawesome/pro-regular-svg-icons';
import { faArrowRight } from '@fortawesome/pro-light-svg-icons/faArrowRight';

import { SafeType } from '@benzinga/safe-await';
import { SessionContext } from '@benzinga/session-context';
import { Authentication, AuthenticationManager, RegisterUser, AuthMode } from '@benzinga/session';
import { GlobalRegisterType, getRegisterTypeByIdentity } from '@benzinga/identity';
import { TrackingManager } from '@benzinga/tracking-manager';
import { TOSAcceptance, UserManager } from '@benzinga/user-manager';

import i18n, { LocaleType } from '@benzinga/translate';
import { useCustomRouter } from '@benzinga/hooks';
import styled from '@benzinga/themetron';
import { Checkbox, ErrorsBox, Icon } from '@benzinga/core-ui';

import { AuthBoxWrapper, AuthInput, AuthPasswordInput } from '../AuthComponents';
import { ReCaptchaComponent } from '../AuthModal/ReCaptcha';
import SocialButton from '../AuthIterations/SocialButton';
import { GOOGLE_SITE_KEY } from '../../config';
import { BtnMain } from '../Login';

export interface RegisterProps {
  initialEmail?: string;
  showUpsell?: boolean;
  onRegister?: (auth: SafeType<Authentication>, authFor: string) => void;
  onSocialClicked?: (socialAuth: string, forceUrl?: string) => void;
  onError?: (errors: string | string[] | null) => void;
  setAuthMode?: (authMode: AuthMode) => void;
  setAuthentication?: (auth: SafeType<Authentication> | null) => void;
  setPhoneNumber?: (phoneNumber: string) => void;
  setOfferEmail?: (email: string) => void;
  isPaywalled?: boolean;
  isRegwalled?: boolean;
}
interface State {
  registerAuth: SafeType<Authentication> | null;
  isRegistering: boolean;
  error: string;
  errors: string[] | null;
}

const sanitizeFormHTML = (formElement: HTMLFormElement): string => {
  const clone = formElement.cloneNode(true) as HTMLFormElement;
  clone.querySelectorAll('input[type="password"]').forEach(el => el.remove());
  clone.querySelectorAll('input').forEach(el => {
    el.removeAttribute('value');
  });

  return clone.outerHTML;
};

interface RegisterUserInput {
  firstname: string;
  lastname: string;
  email: string;
  password: string;
  phoneNumber: string;
}
export const Register = ({
  initialEmail,
  isPaywalled = false,
  isRegwalled = false,
  onError,
  onRegister,
  onSocialClicked,
  setAuthentication,
  setAuthMode,
  setOfferEmail,
  setPhoneNumber,
  showUpsell = false,
}: RegisterProps) => {
  const router = useCustomRouter();
  const [registerInput, setRegisterInput] = useState<RegisterUserInput>({
    email: initialEmail ?? '',
    firstname: '',
    lastname: '',
    password: '',
    phoneNumber: '',
  });
  const [state, setState] = React.useState<State>({
    error: '',
    errors: null,
    isRegistering: false,
    registerAuth: null,
  });
  const [passwordHidden, setPasswordHidden] = useState(true);

  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [reCaptchaNonce, setReCaptchaNonce] = useState<number>(0);
  const [acceptedTermsDate, setAcceptedTermsDate] = useState<Date | null>(null);

  const isFirstNameRequired = router.query?.firstname === 'required';
  const isLastNameRequired = router.query?.lastname === 'required';
  const isPhoneRequired = router.query?.phone === 'required';

  const { t } = useTranslation(['auth', 'common']);
  const session = React.useContext(SessionContext);

  const handleReCaptchaVerify = useCallback((token: string | null) => {
    if (token) {
      setCaptchaToken(token);
    }
  }, []);

  const updateNonce = useCallback(() => {
    setReCaptchaNonce(r => r + 1);
  }, [setReCaptchaNonce]);

  const captureTOSAcceptance = useCallback(
    async (auth: SafeType<Authentication>) => {
      const formElement = document.getElementById('register-form') as HTMLFormElement;
      const htmlContent = formElement ? sanitizeFormHTML(formElement) : '';

      const userAgent = navigator.userAgent;
      const { browser, os } = UAParser(userAgent);
      const token = auth.ok?.key ?? '';
      const userId = auth.ok?.user?.benzingaUid;

      const capture = {
        capture_type: 'registration',
        html_snapshot: htmlContent,
        metadata: {
          browser: browser.name ?? '',
          os: os.name ?? '',
          user_agent: userAgent,
        },
        page_url: window.location.href,
        session_fingerprint: token ?? '',
        tos_accepted: acceptedTermsDate ? true : false,
        tos_accepted_date: acceptedTermsDate ? acceptedTermsDate.toISOString() : '',
        user_id: userId,
      };

      await session.getManager(UserManager).captureTOSAcceptance(capture as TOSAcceptance);
    },
    [session, acceptedTermsDate],
  );

  useEffect(() => {
    updateNonce();
    if (onError && (state.error || state.errors)) {
      onError(state.errors || state.error);
    }
  }, [state.error, state.errors, onError, updateNonce]);

  const handleRegisterPostProcess = async (data: SafeType<Authentication> | null) => {
    await session.getManager(UserManager).setGlobalSetting('default_site', window.location.host);
    await session.getManager(UserManager).setGlobalSetting('accepted_terms_date', acceptedTermsDate);
    if (data) onRegister && onRegister(data, 'register');
  };

  const getRegistrationError = (query: ParsedUrlQuery, input: RegisterUserInput): string => {
    if (input.password.trim() === '') {
      return t('Inputs.Error.is-required', { field: t('Inputs.password', { ns: 'common' }), ns: 'common' });
    } else if (input.email.trim() === '') {
      return t('Inputs.Error.is-required', { field: 'Email', ns: 'common' });
    } else if (query.firstname === 'required' && input.firstname.trim() === '') {
      return 'First Name is required.';
    } else if (query.lastname === 'required' && input.lastname.trim() === '') {
      return 'Last Name is required.';
    } else if (query.phone === 'required' && input.phoneNumber.trim() === '') {
      return 'Phone number is required.';
    } else {
      return '';
    }
  };

  const getNewUser = (
    email: string,
    firstname?: string,
    lastname?: string,
    password?: string,
    phoneNumber?: string,
    captcha?: string,
    globalRegisterType?: GlobalRegisterType,
  ): RegisterUser => {
    const newUser: RegisterUser = {
      email: email,
      password: password ?? '',
    };
    if (firstname) {
      newUser.firstName = firstname;
    }
    if (lastname) {
      newUser.lastName = lastname;
    }
    if (phoneNumber) {
      newUser.phoneNumber = phoneNumber;
    }
    if (captcha) {
      newUser.captcha = captcha;
    }
    if (globalRegisterType) {
      newUser.profileData = {
        globalAccountType: globalRegisterType,
      };
    }

    return newUser;
  };

  const register = async () => {
    setState(prev => ({ ...prev, isRegistering: true }));
    const { email, firstname, lastname, password, phoneNumber } = registerInput;
    const newUser = getNewUser(
      email,
      firstname,
      lastname,
      password,
      phoneNumber,
      captchaToken ?? undefined,
      getRegisterTypeByIdentity(window.location.host, i18n.language as LocaleType) ?? undefined,
    );
    setPhoneNumber && setPhoneNumber(phoneNumber);
    const error = getRegistrationError(router.query, registerInput);
    if (error) {
      setState(prev => ({ ...prev, error, isRegistering: false }));
      return;
    }

    const registerResponse: SafeType<Authentication> = await session
      .getManager(AuthenticationManager)
      .register(newUser, router.query?.utm_source as string);

    if (registerResponse.err) {
      if (registerResponse.err.message) {
        setState(prev => ({ ...prev, error: registerResponse.err.message, isRegistering: false }));
      } else if (registerResponse.err.data instanceof Response) {
        const errors = await registerResponse.err.data.json();
        delete errors.confirm_password;
        setState(prev => ({ ...prev, errors, isRegistering: false }));
      } else {
        setState(prev => ({ ...prev, error: 'Error occured', isRegistering: false }));
      }
      window.parent.postMessage(`register_failed`, '*');
    } else {
      const user = registerResponse.ok?.user;
      if (user && user.accessType !== 'anonymous') {
        await session.getManager(TrackingManager).setUser(user);
        await session.getManager(TrackingManager).trackAuthEvent('register', {
          auth_type: 'email',
          is_paywalled: isPaywalled ?? false,
          is_regwalled: isRegwalled ?? false,
        });
        await captureTOSAcceptance(registerResponse);
      }

      setState(prev => ({ ...prev, isRegistering: false }));
      setAuthentication && setAuthentication(registerResponse);

      if (router.query?.phone === 'required') {
        setAuthMode && setAuthMode('sms-verify');
      } else if (showUpsell) {
        setOfferEmail && setOfferEmail(email);
        setAuthMode && setAuthMode('report-offer');
      } else {
        setAuthMode && setAuthMode('complete');
        handleRegisterPostProcess(registerResponse);
      }
    }
  };

  const togglePasswordHidden = useCallback(() => {
    setPasswordHidden(!passwordHidden);
  }, [passwordHidden, setPasswordHidden]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setRegisterInput(prevState => ({ ...prevState, [name]: value }));
  };

  useEffect(() => {
    setPhoneNumber && setPhoneNumber(registerInput.phoneNumber);
  }, [registerInput.phoneNumber, setPhoneNumber]);

  const handleTermsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAcceptedTermsDate(e.target.checked ? new Date() : null);
  };

  return (
    <>
      <div className="register-social-buttons flex gap-2 mt-8 mb-4">
        <SocialButton onSocialClicked={onSocialClicked} socialType="google" />
        <SocialButton onSocialClicked={onSocialClicked} socialType="apple" />
        <SocialButton onSocialClicked={onSocialClicked} socialType="microsoft" />
      </div>
      <div className="text-center mb-8 relative register-continue-row">
        <div className="w-full h-[1px] bg-white absolute top-1/2 left-0"></div>
        <span className="text-white relative bg-blue-950 px-4">{t('Auth.AuthModal.continue-row')}</span>
      </div>
      <AuthBoxWrapper className="register-auth-box-wrapper">
        <RegisterInputContainer
          onSubmit={e => {
            e.preventDefault();
            register();
          }}
        >
          <div className="input-container mb-2">
            {isFirstNameRequired && (
              <AuthInput
                autoComplete="given-name"
                icon={<Icon className="label-icon" icon={faUser} />}
                label={t('Inputs.first-name', { ns: 'common' })}
                name="firstname"
                onChange={handleInputChange}
                placeholder="John"
                type="text"
                value={registerInput.firstname}
              />
            )}
            {isLastNameRequired && (
              <AuthInput
                autoComplete="family-name"
                icon={<Icon className="label-icon" icon={faUser} />}
                label="Last Name"
                name="lastname"
                onChange={handleInputChange}
                placeholder="Doe"
                type="text"
                value={registerInput.lastname}
              />
            )}
            {isPhoneRequired && (
              <AuthInput
                autoComplete="tel"
                icon={<Icon className="label-icon" icon={faPhone} />}
                label={t('Inputs.phone-number', { ns: 'common' })}
                name="phoneNumber"
                onChange={handleInputChange}
                placeholder="1 (877) 440-9676"
                type="tel"
                value={registerInput.phoneNumber}
              />
            )}
            <AuthInput
              autoComplete="email"
              icon={<Icon className="label-icon" icon={faEnvelope} />}
              id="email"
              label={t('Inputs.email', { ns: 'common' })}
              name="email"
              onChange={handleInputChange}
              placeholder="john.doe@example.com"
              type="email"
              value={registerInput.email}
            />
            <AuthPasswordInput
              autoComplete="new-password"
              hidden={passwordHidden}
              icon={<Icon className="label-icon" icon={faLock} />}
              id="new-password"
              label={t('Inputs.password', { ns: 'common' })}
              name="password"
              onChange={handleInputChange}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') {
                  register();
                }
              }}
              placeholder="***********"
              toggleHidden={togglePasswordHidden}
              value={registerInput.password}
            />
          </div>
          <div className="action-container register-action-container select-none flex flex-col">
            <div className="flex items-center justify-start gap-2 mb-2">
              <Checkbox checked={!!acceptedTermsDate} onChange={handleTermsChange} required type="checkbox" />
              <div className="text-xs text-slate-100">
                I agree to the{' '}
                <a
                  className="text-bzblue-600 underline"
                  href="https://www.benzinga.com/terms-and-conditions"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  className="text-bzblue-600 underline"
                  href="https://www.benzinga.com/privacy-policy"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Privacy Policy
                </a>
                .
              </div>
            </div>
            <BtnMain className="uppercase" isLoading={state.isRegistering} type="submit" variant="flat-blue">
              {t('Auth.Register.button')}
            </BtnMain>
          </div>
          {(state.errors || state.error) && <ErrorsBox error={state.error} errors={state.errors} includeKeys={true} />}
        </RegisterInputContainer>

        <div className="my-4 text-center flex items-center justify-between p-4 font-lg border border-blue-100 rounded register-toggle-box">
          <div>{t('Auth.AuthModal.register.toggle-box.text1')} </div>
          <button
            className="action-text select-none flex gap-2 items-center cursor-pointer"
            onClick={() => {
              setAuthMode && setAuthMode('login');
            }}
            type="button"
          >
            {t('Auth.AuthModal.register.toggle-box.text2')}
            <Icon icon={faArrowRight} />
          </button>
        </div>

        <div className="h-0 invisible">
          <Suspense fallback={<div />}>
            <GoogleReCaptchaProvider
              container={{ element: 'google-recaptcha-reg', parameters: { badge: 'inline', theme: 'light' } }}
              reCaptchaKey={GOOGLE_SITE_KEY}
            >
              <ReCaptchaComponent nonce={reCaptchaNonce} onToken={handleReCaptchaVerify} />
            </GoogleReCaptchaProvider>
          </Suspense>
        </div>
        <div className="flex justify-center w-full min-h-[60px]" id="google-recaptcha-reg"></div>
      </AuthBoxWrapper>
    </>
  );
};

const RegisterInputContainer = styled.form`
  .input-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  .label-icon {
    font-size: 24px;
    color: #99aecc;
    svg {
      height: 24px;
      width: 24px;
    }
  }
`;
